import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, LOAN_PRODUCTS, LOAN_TYPE_LABEL, inr } from "@/lib/api";
import {
  ArrowLeft, User, Phone, Mail, MapPin, FileText, Shield, CalendarClock, Plus, Upload, Download, Trash2, Link2, Copy, MessageCircle, Camera, Printer, UserPlus2, Users2,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon size={14} className="text-[#52525B] mt-1 shrink-0" />
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-[#52525B]">{label}</div>
        <div className="text-sm text-[#09090b]">{value || "—"}</div>
      </div>
    </div>
  );
}

function NewLoanForm({ customer, onDone }) {
  const [f, setF] = useState({ loan_type: "personal_loan", amount: "", tenure_months: 60, purpose: "", submitted_to_bank_contact_id: "", login_date: "", loan_executive_id: "" });
  const { data: banks = [] } = useQuery({ queryKey: ["bank-contacts"], queryFn: async () => (await api.get("/bank-contacts")).data });
  const { data: employees = [] } = useQuery({ queryKey: ["employees-for-loan"], queryFn: async () => (await api.get("/employees")).data.filter((e) => e.active) });
  const chosenBank = banks.find((b) => b.id === f.submitted_to_bank_contact_id);
  const mut = useMutation({
    mutationFn: async () => (await api.post("/loans", {
      customer_id: customer.id, loan_type: f.loan_type,
      amount: Number(f.amount), tenure_months: Number(f.tenure_months), purpose: f.purpose,
      submitted_to_bank: chosenBank?.bank_name || null,
      submitted_to_bank_contact_id: f.submitted_to_bank_contact_id || null,
      login_date: f.login_date || null,
      loan_executive_id: f.loan_executive_id || null,
    })).data,
    onSuccess: () => { toast.success("Loan file created"); onDone(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  return (
    <form onSubmit={(e) => { e.preventDefault(); mut.mutate(); }} className="space-y-3" data-testid="new-loan-form">
      <div>
        <Label>Loan type</Label>
        <Select value={f.loan_type} onValueChange={(v) => setF({ ...f, loan_type: v })}>
          <SelectTrigger data-testid="loan-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>{LOAN_PRODUCTS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Amount (₹)</Label><Input type="number" required value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="loan-amount" /></div>
        <div><Label>Tenure (months)</Label><Input type="number" required value={f.tenure_months} onChange={(e) => setF({ ...f, tenure_months: e.target.value })} data-testid="loan-tenure" /></div>
      </div>
      <div><Label>Purpose</Label><Input value={f.purpose} onChange={(e) => setF({ ...f, purpose: e.target.value })} /></div>
      <div>
        <Label>Submitting to Bank / NBFC</Label>
        <Select value={f.submitted_to_bank_contact_id} onValueChange={(v) => setF({ ...f, submitted_to_bank_contact_id: v })}>
          <SelectTrigger data-testid="loan-bank"><SelectValue placeholder="Search & select bank contact" /></SelectTrigger>
          <SelectContent>
            {banks.length === 0 && <div className="px-3 py-2 text-xs text-[#52525B]">No bank contacts yet. Add from Settings.</div>}
            {banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.bank_name} · {b.executive_name || "—"}</SelectItem>)}
          </SelectContent>
        </Select>
        {chosenBank && (
          <div className="mt-2 text-xs grid grid-cols-2 gap-1 text-[#52525B]" data-testid="bank-details">
            <div>Executive: <span className="text-[#06152D]">{chosenBank.executive_name || "—"}</span></div>
            <div>Mobile: <span className="font-mono-num text-[#06152D]">{chosenBank.executive_mobile || "—"}</span></div>
            <div>Branch: <span className="text-[#06152D]">{chosenBank.branch || "—"}</span></div>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Login date</Label><Input type="date" value={f.login_date} onChange={(e) => setF({ ...f, login_date: e.target.value })} data-testid="loan-login-date" /></div>
        <div>
          <Label>Loan Executive</Label>
          <Select value={f.loan_executive_id} onValueChange={(v) => setF({ ...f, loan_executive_id: v })}>
            <SelectTrigger data-testid="loan-exec"><SelectValue placeholder="Assign" /></SelectTrigger>
            <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.employee_id || "—"} · {e.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <button type="submit" disabled={mut.isPending} className="w-full bg-[#0052FF] hover:bg-[#003FCC] text-white font-semibold px-4 py-2.5 rounded-full disabled:opacity-60" data-testid="loan-submit">
        {mut.isPending ? "Saving…" : "Create Loan File"}
      </button>
    </form>
  );
}

function DocUploadForm({ customer, loanType, onDone }) {
  const [file, setFile] = useState(null);
  const [docType, setDocType] = useState("aadhaar");
  const { data: config } = useQuery({
    queryKey: ["doc-config", loanType],
    queryFn: async () => (await api.get(`/doc-configs/${loanType}`)).data,
    enabled: !!loanType,
  });
  const mut = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append("customer_id", customer.id);
      fd.append("doc_type", docType);
      fd.append("file", file);
      const { data } = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      return data;
    },
    onSuccess: () => { toast.success("Document uploaded"); onDone(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Upload failed"),
  });
  const reqs = config?.requirements || [];
  return (
    <form onSubmit={(e) => { e.preventDefault(); if (!file) { toast.error("Choose a file"); return; } mut.mutate(); }} className="space-y-3" data-testid="doc-upload-form">
      <div>
        <Label>Document type</Label>
        <Select value={docType} onValueChange={setDocType}>
          <SelectTrigger data-testid="doc-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {reqs.length ? reqs.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>) :
              ["aadhaar","pan","salary_slip","bank_statement","other"].map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)
            }
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>File</Label>
        <Input type="file" onChange={(e) => setFile(e.target.files?.[0])} data-testid="doc-file" />
      </div>
      <button type="submit" disabled={mut.isPending} className="w-full bg-[#0052FF] hover:bg-[#003FCC] text-white font-semibold px-4 py-2.5 rounded-full disabled:opacity-60" data-testid="doc-upload-submit">
        {mut.isPending ? "Uploading…" : "Upload Document"}
      </button>
    </form>
  );
}

async function downloadDoc(docId, filename) {
  const { data } = await api.get(`/documents/${docId}/download`, { responseType: "blob" });
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url; a.download = filename || "document"; a.click();
  URL.revokeObjectURL(url);
}

function DownloadLinkDialog({ customer, docs }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [password, setPassword] = useState("");
  const [expires, setExpires] = useState(24);
  const [note, setNote] = useState("");
  const [maxDl, setMaxDl] = useState("");
  const [result, setResult] = useState(null);

  React.useEffect(() => {
    if (open && step === 1 && selectedIds.length === 0) {
      // Preselect all docs by default
      setSelectedIds(docs.map((d) => d.id));
    }
  }, [open, step, docs]);

  const toggle = (id) => setSelectedIds((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const mut = useMutation({
    mutationFn: async () => (await api.post("/download-links", {
      customer_id: customer.id,
      document_ids: selectedIds,
      password,
      expires_in_hours: Number(expires),
      max_downloads: maxDl ? Number(maxDl) : null,
      note: note || null,
    })).data,
    onSuccess: (data) => { setResult(data); setStep(2); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const publicUrl = result ? `${window.location.origin}/download/${result.token}` : "";
  const shareMsg = (customer) => encodeURIComponent(
    `Hi ${customer.full_name.split(" ")[0]}, please download your documents from this secure Loan Junction link:\n${publicUrl}\n\nPassword will be shared separately. Expires ${result?.expires_at ? new Date(result.expires_at).toLocaleString("en-IN") : ""}.`
  );
  const copy = async () => { await navigator.clipboard.writeText(publicUrl); toast.success("Link copied"); };
  const reset = () => { setStep(1); setResult(null); setSelectedIds(docs.map((d) => d.id)); setPassword(""); setExpires(24); setNote(""); setMaxDl(""); };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <button className="border border-[#0052FF] text-[#0052FF] hover:bg-[#EBF2FF] text-sm font-semibold px-4 py-2 rounded-full inline-flex items-center gap-1.5" data-testid="gen-download-link-btn">
          <Link2 size={14} /> Generate Download Link
        </button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-lg">
        <DialogHeader><DialogTitle>Secure document download link</DialogTitle></DialogHeader>
        {step === 1 && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (selectedIds.length === 0) { toast.error("Select at least one document"); return; }
              if (!password || password.length < 4) { toast.error("Set a password (min 4 chars)"); return; }
              mut.mutate();
            }}
            className="space-y-3"
            data-testid="dl-form-step1"
          >
            <div>
              <div className="flex items-center justify-between">
                <Label>Documents to share ({selectedIds.length}/{docs.length})</Label>
                <button type="button" onClick={() => setSelectedIds(selectedIds.length === docs.length ? [] : docs.map((d) => d.id))} className="text-xs text-[#0052FF] font-semibold" data-testid="dl-toggle-all">
                  {selectedIds.length === docs.length ? "Clear all" : "Select all"}
                </button>
              </div>
              {docs.length === 0 ? (
                <div className="text-xs text-[#52525B] p-3 bg-[#F4F4F5] rounded-lg">No documents uploaded yet. Upload some first.</div>
              ) : (
                <div className="mt-2 max-h-48 overflow-y-auto border border-[#E4E4E7] rounded-lg divide-y divide-[#E4E4E7]">
                  {docs.map((d) => (
                    <label key={d.id} className="flex items-center gap-2 p-2 text-xs hover:bg-[#F4F4F5] cursor-pointer" data-testid={`dl-doc-${d.id}`}>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(d.id)}
                        onChange={() => toggle(d.id)}
                        data-testid={`dl-doc-check-${d.id}`}
                      />
                      <span className="font-semibold uppercase tracking-wider text-[#52525B] text-[10px] w-24">{d.doc_type}</span>
                      <span className="flex-1 truncate">{d.original_filename}</span>
                      <span className="text-[#94A3B8]">{(d.size / 1024).toFixed(0)} KB</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Password / Lock Code *</Label>
                <Input type="text" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 4 chars" data-testid="dl-password" />
              </div>
              <div>
                <Label>Expires in</Label>
                <Select value={String(expires)} onValueChange={(v) => setExpires(Number(v))}>
                  <SelectTrigger data-testid="dl-expires"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0.5">30 minutes</SelectItem>
                    <SelectItem value="1">1 hour</SelectItem>
                    <SelectItem value="6">6 hours</SelectItem>
                    <SelectItem value="24">24 hours (1 day)</SelectItem>
                    <SelectItem value="72">3 days</SelectItem>
                    <SelectItem value="168">7 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Max total downloads (optional)</Label>
                <Input type="number" value={maxDl} onChange={(e) => setMaxDl(e.target.value)} placeholder="e.g. 5" data-testid="dl-max" />
              </div>
              <div>
                <Label>Note (optional)</Label>
                <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Shown on the download page" data-testid="dl-note" />
              </div>
            </div>
            <button type="submit" disabled={mut.isPending || docs.length === 0} className="w-full bg-[#0052FF] hover:bg-[#003FCC] text-white font-semibold px-4 py-2.5 rounded-full disabled:opacity-60" data-testid="dl-submit">
              {mut.isPending ? "Generating…" : "Generate Download Link"}
            </button>
            <div className="text-[10px] text-[#52525B]">Link is password-protected and exposes ONLY the selected documents. No other customer data (Customer 360, contacts, loans, KYC) is accessible from this link.</div>
          </form>
        )}
        {step === 2 && result && (
          <div className="space-y-3" data-testid="dl-result">
            <div className="text-xs text-emerald-700 font-semibold">✓ Download link created · {result.document_count} document(s) · expires {new Date(result.expires_at).toLocaleString("en-IN")}</div>
            <div className="p-3 bg-[#F4F4F5] border border-[#E4E4E7] rounded-lg break-all text-xs font-mono-num" data-testid="dl-link-value">{publicUrl}</div>
            <div className="grid grid-cols-4 gap-2">
              <a
                href={`https://wa.me/${customer.phone.replace(/\D/g, "").replace(/^0/, "91").padStart(12, "91")}?text=${shareMsg(customer)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-center bg-[#25D366] hover:bg-[#1EBE5A] text-white text-xs font-semibold py-2 rounded-full inline-flex items-center justify-center gap-1"
                data-testid="dl-share-wa"
              >
                <MessageCircle size={12} /> WhatsApp
              </a>
              <a
                href={`mailto:${customer.email || ""}?subject=${encodeURIComponent(`Your Loan Junction documents — ${customer.customer_id}`)}&body=${shareMsg(customer)}`}
                className="text-center bg-[#0052FF] hover:bg-[#003FCC] text-white text-xs font-semibold py-2 rounded-full"
                data-testid="dl-share-email"
              >
                Email
              </a>
              <a href={`sms:${customer.phone}?body=${shareMsg(customer)}`} className="text-center bg-[#06152D] hover:bg-[#0b2350] text-white text-xs font-semibold py-2 rounded-full" data-testid="dl-share-sms">SMS</a>
              <button onClick={copy} className="bg-white border border-[#E4E4E7] hover:bg-[#EBF2FF] text-[#06152D] text-xs font-semibold py-2 rounded-full inline-flex items-center justify-center gap-1" data-testid="dl-copy">
                <Copy size={12} /> Copy
              </button>
            </div>
            <div className="text-[10px] text-[#52525B]">Share the password separately (do not send in the same channel as the link).</div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EditProfileDialog({ customer, onSaved }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({
    full_name: customer.full_name || "",
    phone: customer.phone || "",
    alt_phone: customer.alt_phone || "",
    email: customer.email || "",
    address: customer.address || "",
    city: customer.city || "",
    pincode: customer.pincode || "",
  });
  React.useEffect(() => {
    if (open) {
      setF({
        full_name: customer.full_name || "",
        phone: customer.phone || "",
        alt_phone: customer.alt_phone || "",
        email: customer.email || "",
        address: customer.address || "",
        city: customer.city || "",
        pincode: customer.pincode || "",
      });
    }
  }, [open, customer]);
  const mut = useMutation({
    mutationFn: async () => (await api.patch(`/customers/${customer.id}`, {
      full_name: f.full_name,
      phone: f.phone,
      alt_phone: f.alt_phone || null,
      email: f.email || null,
      address: f.address || null,
      city: f.city || null,
      pincode: f.pincode || null,
    })).data,
    onSuccess: () => { toast.success("Customer profile updated"); onSaved?.(); setOpen(false); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="border border-[#E4E4E7] hover:border-[#0052FF] text-[#06152D] text-sm font-semibold px-3 py-1.5 rounded-full inline-flex items-center gap-1.5" data-testid="edit-profile-btn">
          Edit Profile
        </button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-lg">
        <DialogHeader><DialogTitle>Edit customer profile</DialogTitle></DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!f.full_name || !f.phone) { toast.error("Name and mobile are required"); return; }
            mut.mutate();
          }}
          className="space-y-3"
          data-testid="edit-profile-form"
        >
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Customer Name *</Label><Input value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} data-testid="ep-name" /></div>
            <div><Label>Mobile Number *</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="ep-phone" /></div>
            <div><Label>Alternate Mobile</Label><Input value={f.alt_phone} onChange={(e) => setF({ ...f, alt_phone: e.target.value })} data-testid="ep-alt-phone" /></div>
            <div className="col-span-2"><Label>Email Address</Label><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="ep-email" /></div>
            <div className="col-span-2"><Label>Address</Label><Textarea rows={2} value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} data-testid="ep-address" /></div>
            <div><Label>City</Label><Input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} data-testid="ep-city" /></div>
            <div><Label>PIN Code</Label><Input value={f.pincode} onChange={(e) => setF({ ...f, pincode: e.target.value })} data-testid="ep-pincode" /></div>
          </div>
          <button type="submit" disabled={mut.isPending} className="w-full bg-[#0052FF] hover:bg-[#003FCC] text-white font-semibold px-4 py-2.5 rounded-full disabled:opacity-60" data-testid="ep-submit">
            {mut.isPending ? "Saving…" : "Save to Customer Master"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function CustomerDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const { data: customer, refetch: refetchCust } = useQuery({ queryKey: ["customer", id], queryFn: async () => (await api.get(`/customers/${id}`)).data });
  const { data: loans = [], refetch: refetchLoans } = useQuery({ queryKey: ["loans", id], queryFn: async () => (await api.get("/loans", { params: { customer_id: id } })).data });
  const { data: docs = [], refetch: refetchDocs } = useQuery({ queryKey: ["docs", id], queryFn: async () => (await api.get("/documents", { params: { customer_id: id } })).data });
  const { data: insurances = [], refetch: refetchIns } = useQuery({ queryKey: ["insurance", id], queryFn: async () => (await api.get("/insurance", { params: { customer_id: id } })).data });
  const { data: followups = [], refetch: refetchFu } = useQuery({ queryKey: ["fu", id], queryFn: async () => (await api.get("/followups", { params: { customer_id: id } })).data });
  const { data: timeline = [] } = useQuery({ queryKey: ["timeline", id], queryFn: async () => (await api.get(`/customers/${id}/timeline`)).data });
  const { data: originLead } = useQuery({
    queryKey: ["origin-lead", customer?.origin_lead_id],
    queryFn: async () => {
      if (!customer?.origin_lead_id) return null;
      const all = await api.get("/leads");
      return all.data.find((l) => l.id === customer.origin_lead_id) || null;
    },
    enabled: !!customer?.origin_lead_id,
  });

  // Unique vehicles across all insurance policies
  const vehicles = React.useMemo(() => {
    const map = new Map();
    insurances.forEach((i) => {
      if (i.vehicle_number) {
        const key = i.vehicle_number.toUpperCase();
        const prev = map.get(key);
        if (!prev || new Date(i.end_date || 0) > new Date(prev.latest_expiry || 0)) {
          map.set(key, {
            vehicle_number: i.vehicle_number,
            vehicle_type: i.vehicle_type || prev?.vehicle_type,
            latest_provider: i.provider,
            latest_policy: i.policy_number,
            latest_expiry: i.end_date,
            latest_idv: i.idv,
            policy_count: (prev?.policy_count || 0) + 1,
          });
        } else if (prev) {
          prev.policy_count += 1;
        }
      }
    });
    return Array.from(map.values());
  }, [insurances]);

  const [loanOpen, setLoanOpen] = useState(false);
  const [docOpen, setDocOpen] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [coAppOpen, setCoAppOpen] = useState(false);
  const [refOpen, setRefOpen] = useState(false);
  const primaryLoanType = loans[0]?.loan_type || "personal_loan";

  const uploadPhoto = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/customers/${customer.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo uploaded");
      refetchCust(); refetchDocs();
      setPhotoOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };

  const printPdf = async () => {
    try {
      const res = await api.get(`/customers/${customer.id}/statement-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `customer_${customer.customer_id}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("PDF export failed"); }
  };

  const saveCoApplicants = async (newList) => {
    try {
      await api.patch(`/customers/${customer.id}`, { co_applicants: newList });
      toast.success("Co-applicants updated");
      refetchCust();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const saveReferences = async (newList) => {
    try {
      await api.patch(`/customers/${customer.id}`, { references: newList });
      toast.success("References updated");
      refetchCust();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!customer) return <div className="text-[#52525B]">Loading…</div>;

  return (
    <div>
      <div className="flex items-center justify-between">
        <Link to="/admin/customers" className="text-sm text-[#52525B] hover:text-[#0052FF] inline-flex items-center gap-1" data-testid="back-to-customers">
          <ArrowLeft size={14} /> Customer 360
        </Link>
        <div className="flex gap-2">
          <EditProfileDialog customer={customer} onSaved={() => refetchCust()} />
          <button onClick={printPdf} className="border border-[#E4E4E7] hover:border-[#0052FF] text-[#06152D] text-sm font-semibold px-3 py-1.5 rounded-full inline-flex items-center gap-1.5" data-testid="customer-pdf-btn">
            <Download size={14} /> Save as PDF
          </button>
          <button onClick={() => window.print()} className="border border-[#E4E4E7] hover:border-[#0052FF] text-[#06152D] text-sm font-semibold px-3 py-1.5 rounded-full inline-flex items-center gap-1.5" data-testid="customer-print-btn">
            <Printer size={14} /> Print
          </button>
        </div>
      </div>

      <div className="mt-3 bg-white border border-[#E4E4E7] rounded-2xl p-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3">
              {customer.photo_document_id ? (
                <img
                  src="#"
                  onError={(e) => { e.target.style.display = "none"; }}
                  onLoad={() => {}}
                  alt=""
                  className="hidden"
                />
              ) : null}
              <button onClick={() => setPhotoOpen(true)} className="h-14 w-14 rounded-full bg-[#EBF2FF] text-[#0052FF] flex items-center justify-center font-bold border-2 border-dashed border-[#0052FF]/30 hover:bg-[#dce8ff]" data-testid="customer-photo-btn" title="Upload photo">
                {customer.photo_document_id ? <Camera size={18} /> : customer.full_name?.[0]?.toUpperCase()}
              </button>
              <div>
                <div className="font-display text-2xl font-bold text-[#06152D]">{customer.full_name}</div>
                <div className="text-xs text-[#52525B] font-mono-num">{customer.customer_id}</div>
                {customer.branch && <div className="text-[10px] text-[#94A3B8]">Branch: {customer.branch}</div>}
              </div>
            </div>
            <div className="mt-4 flex gap-2 flex-wrap">
              {customer.is_loan_customer && <Badge className="bg-blue-100 text-blue-800" variant="secondary">Loan customer</Badge>}
              {customer.is_insurance_customer && <Badge className="bg-violet-100 text-violet-800" variant="secondary">Insurance customer</Badge>}
              {originLead && (
                <Badge className="bg-amber-100 text-amber-800" variant="secondary" data-testid="origin-lead-badge">
                  Origin: {originLead.lead_id}
                </Badge>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <InfoRow icon={Phone} label="Phone" value={customer.phone} />
            <InfoRow icon={Phone} label="Alt phone" value={customer.alt_phone} />
            <InfoRow icon={Mail} label="Email" value={customer.email} />
            <InfoRow icon={MapPin} label="City" value={customer.city} />
            <InfoRow icon={User} label="PAN" value={customer.pan} />
            <InfoRow icon={User} label="Aadhaar" value={customer.aadhaar} />
          </div>
        </div>
        {originLead && (
          <div className="mt-4 pt-4 border-t border-[#E4E4E7]" data-testid="origin-lead-card">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-[#52525B]">Original lead details</div>
            <div className="mt-2 grid sm:grid-cols-4 gap-3 text-sm">
              <div><span className="text-[#52525B]">Lead ID: </span><span className="font-mono-num font-semibold text-[#06152D]">{originLead.lead_id}</span></div>
              <div><span className="text-[#52525B]">Interest: </span><span className="text-[#06152D]">{(originLead.loan_type || "").replace(/_/g, " ")}</span></div>
              <div><span className="text-[#52525B]">Amount: </span><span className="font-mono-num text-[#06152D]">{originLead.loan_amount ? "₹" + Number(originLead.loan_amount).toLocaleString("en-IN") : "—"}</span></div>
              <div><span className="text-[#52525B]">Source: </span><span className="text-[#06152D]">{originLead.source}</span></div>
            </div>
          </div>
        )}
      </div>

      <Tabs defaultValue="loans" className="mt-6">
        <TabsList className="bg-white border border-[#E4E4E7] flex-wrap h-auto">
          <TabsTrigger value="loans" data-testid="tab-loans">Loan Files ({loans.length})</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents ({docs.length})</TabsTrigger>
          <TabsTrigger value="insurance" data-testid="tab-insurance">Insurance ({insurances.length})</TabsTrigger>
          <TabsTrigger value="vehicles" data-testid="tab-vehicles">Vehicles ({vehicles.length})</TabsTrigger>
          <TabsTrigger value="co-applicants" data-testid="tab-co-applicants">Co-Applicants ({(customer.co_applicants || []).length})</TabsTrigger>
          <TabsTrigger value="references" data-testid="tab-references">References ({(customer.references || []).length})</TabsTrigger>
          <TabsTrigger value="followups" data-testid="tab-followups">Follow-ups ({followups.length})</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
        </TabsList>

        <TabsContent value="loans" className="mt-4">
          <div className="flex justify-end mb-3">
            <Dialog open={loanOpen} onOpenChange={setLoanOpen}>
              <DialogTrigger asChild>
                <button className="bg-[#0052FF] hover:bg-[#003FCC] text-white text-sm font-semibold px-4 py-2 rounded-full inline-flex items-center gap-1.5" data-testid="add-loan-btn"><Plus size={14} /> New Loan File</button>
              </DialogTrigger>
              <DialogContent className="bg-white">
                <DialogHeader><DialogTitle>New Loan File · {customer.full_name}</DialogTitle></DialogHeader>
                <NewLoanForm customer={customer} onDone={() => { refetchLoans(); setLoanOpen(false); }} />
              </DialogContent>
            </Dialog>
          </div>
          <div className="bg-white border border-[#E4E4E7] rounded-xl overflow-hidden">
            <table className="w-full lj-table" data-testid="loans-table">
              <thead><tr><th>File No.</th><th>App ID</th><th>Type</th><th>Amount</th><th>Status</th><th>Bank</th><th>Created</th></tr></thead>
              <tbody>
                {loans.length === 0 && <tr><td colSpan="7" className="text-center text-[#52525B] py-6">No loan files yet.</td></tr>}
                {loans.map((l) => (
                  <tr key={l.id} data-testid={`loan-row-${l.id}`}>
                    <td className="font-mono-num text-xs">{l.file_number || "—"}</td>
                    <td className="font-mono-num text-xs">{l.application_id}</td>
                    <td>{LOAN_TYPE_LABEL[l.loan_type]}</td>
                    <td className="font-mono-num">{inr(l.amount)}</td>
                    <td><Badge variant="secondary" className="bg-slate-100">{l.status}</Badge></td>
                    <td className="text-xs">{l.submitted_to_bank || "—"}</td>
                    <td className="text-xs text-[#52525B]">{new Date(l.created_at).toLocaleDateString("en-IN")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="documents" className="mt-4">
          <div className="flex justify-end gap-2 mb-3">
            <DownloadLinkDialog customer={customer} docs={docs} />
            <Dialog open={docOpen} onOpenChange={setDocOpen}>
              <DialogTrigger asChild>
                <button className="bg-[#0052FF] hover:bg-[#003FCC] text-white text-sm font-semibold px-4 py-2 rounded-full inline-flex items-center gap-1.5" data-testid="upload-doc-btn"><Upload size={14} /> Upload Document</button>
              </DialogTrigger>
              <DialogContent className="bg-white">
                <DialogHeader><DialogTitle>Upload document</DialogTitle></DialogHeader>
                <DocUploadForm customer={customer} loanType={primaryLoanType} onDone={() => { refetchDocs(); setDocOpen(false); }} />
              </DialogContent>
            </Dialog>
          </div>
          <div className="bg-white border border-[#E4E4E7] rounded-xl overflow-hidden">
            <table className="w-full lj-table" data-testid="docs-table">
              <thead><tr><th>Type</th><th>Filename</th><th>Size</th><th>Uploaded</th><th></th></tr></thead>
              <tbody>
                {docs.length === 0 && <tr><td colSpan="5" className="text-center text-[#52525B] py-6">No documents yet.</td></tr>}
                {docs.map((d) => (
                  <tr key={d.id} data-testid={`doc-row-${d.id}`}>
                    <td className="text-xs font-semibold uppercase tracking-wider text-[#52525B]">{d.doc_type}</td>
                    <td>{d.original_filename}</td>
                    <td className="text-xs text-[#52525B]">{(d.size / 1024).toFixed(1)} KB</td>
                    <td className="text-xs text-[#52525B]">{new Date(d.uploaded_at).toLocaleDateString("en-IN")}</td>
                    <td>
                      <button onClick={() => downloadDoc(d.id, d.original_filename)} className="text-[#0052FF] hover:text-[#003FCC] inline-flex items-center gap-1 text-xs font-semibold" data-testid={`doc-download-${d.id}`}>
                        <Download size={12} /> Download
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="insurance" className="mt-4">
          <div className="bg-white border border-[#E4E4E7] rounded-xl overflow-hidden">
            <table className="w-full lj-table" data-testid="ins-table">
              <thead><tr><th>Type</th><th>Provider</th><th>Policy No.</th><th>Vehicle No.</th><th>Premium</th><th>Expiry</th></tr></thead>
              <tbody>
                {insurances.length === 0 && <tr><td colSpan="6" className="text-center text-[#52525B] py-6">No insurance policies. Add from Insurance module.</td></tr>}
                {insurances.map((i) => (
                  <tr key={i.id}>
                    <td>{i.policy_type}</td>
                    <td>{i.provider}</td>
                    <td className="font-mono-num text-xs">{i.policy_number || "—"}</td>
                    <td className="font-mono-num text-xs">{i.vehicle_number || "—"}</td>
                    <td className="font-mono-num">{i.premium ? inr(i.premium) : "—"}</td>
                    <td className="text-xs">{i.end_date || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="vehicles" className="mt-4">
          <div className="bg-white border border-[#E4E4E7] rounded-xl overflow-hidden">
            <table className="w-full lj-table" data-testid="vehicles-table">
              <thead><tr><th>Vehicle No.</th><th>Type</th><th>Latest Provider</th><th>Latest Policy No.</th><th>IDV</th><th>Expires</th><th>Policies</th></tr></thead>
              <tbody>
                {vehicles.length === 0 && <tr><td colSpan="7" className="text-center text-[#52525B] py-6">No vehicles yet. Add a vehicle insurance policy to link one.</td></tr>}
                {vehicles.map((v) => (
                  <tr key={v.vehicle_number} data-testid={`vehicle-row-${v.vehicle_number}`}>
                    <td className="font-mono-num font-semibold text-[#06152D]">{v.vehicle_number}</td>
                    <td>{v.vehicle_type || "—"}</td>
                    <td>{v.latest_provider}</td>
                    <td className="font-mono-num text-xs">{v.latest_policy || "—"}</td>
                    <td className="font-mono-num">{v.latest_idv ? inr(v.latest_idv) : "—"}</td>
                    <td className="text-xs">{v.latest_expiry || "—"}</td>
                    <td><Badge variant="secondary" className="text-xs">{v.policy_count} policies</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="followups" className="mt-4">
          <div className="bg-white border border-[#E4E4E7] rounded-xl overflow-hidden">
            <table className="w-full lj-table">
              <thead><tr><th>Date &amp; Time</th><th>Executive</th><th>Type</th><th>Status</th><th>Outcome</th><th>Response</th><th>Next</th><th>Remarks</th></tr></thead>
              <tbody>
                {followups.length === 0 && <tr><td colSpan="8" className="text-center text-[#52525B] py-6">No follow-ups yet.</td></tr>}
                {followups.map((f) => (
                  <tr key={f.id}>
                    <td className="text-xs font-mono-num">{new Date(f.scheduled_at).toLocaleString("en-IN")}</td>
                    <td className="text-xs">{f.executive_name || "—"}</td>
                    <td className="text-xs">{f.channel}</td>
                    <td><Badge variant="secondary">{f.status}</Badge></td>
                    <td className="text-xs">{f.outcome || "—"}</td>
                    <td className="text-xs">{f.customer_response || "—"}</td>
                    <td className="text-xs">{f.next_followup_date ? new Date(f.next_followup_date).toLocaleDateString("en-IN") : "—"}</td>
                    <td className="text-xs">{f.remarks || f.notes || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="co-applicants" className="mt-4">
          <CoApplicantsEditor list={customer.co_applicants || []} onSave={saveCoApplicants} />
        </TabsContent>

        <TabsContent value="references" className="mt-4">
          <ReferencesEditor list={customer.references || []} onSave={saveReferences} />
        </TabsContent>

        <TabsContent value="timeline" className="mt-4">
          <div className="bg-white border border-[#E4E4E7] rounded-xl p-6">
            {timeline.length === 0 && <div className="text-sm text-[#52525B]">No activity yet.</div>}
            <ul className="space-y-4">
              {timeline.map((t) => (
                <li key={t.id} className="flex gap-3">
                  <div className="h-8 w-8 rounded-full bg-[#EBF2FF] text-[#0052FF] flex items-center justify-center text-xs font-bold shrink-0">{(t.actor_name || "?")[0]}</div>
                  <div className="min-w-0">
                    <div className="text-sm text-[#09090b]"><span className="font-semibold">{t.actor_name}</span> · <span className="text-[#52525B]">{t.action}</span></div>
                    <div className="text-xs text-[#52525B] mt-0.5">{t.detail}</div>
                    <div className="text-[10px] text-[#94A3B8] mt-1">{new Date(t.created_at).toLocaleString("en-IN")}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </TabsContent>
      </Tabs>

      {/* Photo upload dialog */}
      <Dialog open={photoOpen} onOpenChange={setPhotoOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>Upload Customer Photo</DialogTitle></DialogHeader>
          <Input type="file" accept="image/*" onChange={(e) => uploadPhoto(e.target.files?.[0])} data-testid="customer-photo-file" />
          <p className="text-xs text-[#52525B]">JPG/PNG · max 5 MB · displayed in Customer 360 &amp; PDF statement</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CoApplicantsEditor({ list, onSave }) {
  const [items, setItems] = useState(list);
  React.useEffect(() => { setItems(list); }, [list]);
  const add = () => setItems([...items, { name: "", relation: "", phone: "", pan: "", aadhaar: "", dob: "", occupation: "", monthly_income: "", address: "" }]);
  const upd = (i, k, v) => { const c = [...items]; c[i] = { ...c[i], [k]: v }; setItems(c); };
  const del = (i) => setItems(items.filter((_, idx) => idx !== i));
  return (
    <div className="bg-white border border-[#E4E4E7] rounded-xl p-4" data-testid="co-applicants-editor">
      <div className="flex justify-between items-center mb-3">
        <p className="text-sm text-[#52525B]">Add multiple co-applicants with KYC details.</p>
        <div className="flex gap-2">
          <button onClick={add} className="text-xs font-semibold text-[#0052FF] inline-flex items-center gap-1" data-testid="co-app-add"><UserPlus2 size={12} /> Add Co-Applicant</button>
          <button onClick={() => onSave(items)} className="text-xs font-semibold bg-[#0052FF] text-white px-3 py-1 rounded-full" data-testid="co-app-save">Save</button>
        </div>
      </div>
      {items.length === 0 && <div className="text-center text-[#52525B] text-sm py-4">No co-applicants.</div>}
      <div className="space-y-3">
        {items.map((c, i) => (
          <div key={i} className="border border-[#E4E4E7] rounded-lg p-3 grid grid-cols-3 gap-2 relative" data-testid={`co-app-${i}`}>
            <button onClick={() => del(i)} className="absolute top-1 right-1 text-red-600" data-testid={`co-app-del-${i}`}><Trash2 size={12} /></button>
            <div><Label className="text-xs">Name *</Label><Input value={c.name} onChange={(e) => upd(i, "name", e.target.value)} /></div>
            <div><Label className="text-xs">Relation</Label><Input value={c.relation || ""} onChange={(e) => upd(i, "relation", e.target.value)} /></div>
            <div><Label className="text-xs">Phone</Label><Input value={c.phone || ""} onChange={(e) => upd(i, "phone", e.target.value)} /></div>
            <div><Label className="text-xs">PAN</Label><Input value={c.pan || ""} onChange={(e) => upd(i, "pan", e.target.value)} /></div>
            <div><Label className="text-xs">Aadhaar</Label><Input value={c.aadhaar || ""} onChange={(e) => upd(i, "aadhaar", e.target.value)} /></div>
            <div><Label className="text-xs">DOB</Label><Input type="date" value={c.dob || ""} onChange={(e) => upd(i, "dob", e.target.value)} /></div>
            <div><Label className="text-xs">Occupation</Label><Input value={c.occupation || ""} onChange={(e) => upd(i, "occupation", e.target.value)} /></div>
            <div><Label className="text-xs">Monthly Income</Label><Input type="number" value={c.monthly_income || ""} onChange={(e) => upd(i, "monthly_income", e.target.value ? Number(e.target.value) : null)} /></div>
            <div className="col-span-3"><Label className="text-xs">Address</Label><Input value={c.address || ""} onChange={(e) => upd(i, "address", e.target.value)} /></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReferencesEditor({ list, onSave }) {
  const [items, setItems] = useState(list);
  React.useEffect(() => { setItems(list); }, [list]);
  const add = () => setItems([...items, { name: "", relation: "", phone: "", address: "", notes: "" }]);
  const upd = (i, k, v) => { const c = [...items]; c[i] = { ...c[i], [k]: v }; setItems(c); };
  const del = (i) => setItems(items.filter((_, idx) => idx !== i));
  return (
    <div className="bg-white border border-[#E4E4E7] rounded-xl p-4" data-testid="references-editor">
      <div className="flex justify-between items-center mb-3">
        <p className="text-sm text-[#52525B]">Add multiple reference contacts.</p>
        <div className="flex gap-2">
          <button onClick={add} className="text-xs font-semibold text-[#0052FF] inline-flex items-center gap-1" data-testid="ref-add"><Users2 size={12} /> Add Reference</button>
          <button onClick={() => onSave(items)} className="text-xs font-semibold bg-[#0052FF] text-white px-3 py-1 rounded-full" data-testid="ref-save">Save</button>
        </div>
      </div>
      {items.length === 0 && <div className="text-center text-[#52525B] text-sm py-4">No references.</div>}
      <div className="space-y-3">
        {items.map((r, i) => (
          <div key={i} className="border border-[#E4E4E7] rounded-lg p-3 grid grid-cols-3 gap-2 relative" data-testid={`ref-${i}`}>
            <button onClick={() => del(i)} className="absolute top-1 right-1 text-red-600" data-testid={`ref-del-${i}`}><Trash2 size={12} /></button>
            <div><Label className="text-xs">Name *</Label><Input value={r.name} onChange={(e) => upd(i, "name", e.target.value)} /></div>
            <div><Label className="text-xs">Relation</Label><Input value={r.relation || ""} onChange={(e) => upd(i, "relation", e.target.value)} /></div>
            <div><Label className="text-xs">Phone</Label><Input value={r.phone || ""} onChange={(e) => upd(i, "phone", e.target.value)} /></div>
            <div className="col-span-3"><Label className="text-xs">Address</Label><Input value={r.address || ""} onChange={(e) => upd(i, "address", e.target.value)} /></div>
            <div className="col-span-3"><Label className="text-xs">Notes</Label><Input value={r.notes || ""} onChange={(e) => upd(i, "notes", e.target.value)} /></div>
          </div>
        ))}
      </div>
    </div>
  );
}
